<script lang="ts">
	import OnboardingStepContainer from './OnboardingStepContainer.svelte';
	import { Alert, Icon, Input, Popover, Text, Toggle } from '@Shelly/shelly-design-system/index';
	import { format } from 'svelte-i18n';
	import { valueOutOfRange } from 'src/helpers/utilities';
	import { app, os } from 'src/stores/session/native-bridge';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import { control_bluetooth_device_ios } from 'src/services/native_container_requests_ios';
	import { control_bluetooth_device_android } from 'src/services/native_container_requests_android';
	import DynamicAlert from 'src/components/DevicesSettings/SettingsGBLE/DynamicAlert.svelte';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	export let device;
	export let deviceData;
	export let initialized = false;
	export let showExpiredMsg = false;

	deviceData.measureVibration = device?.measureVibration || deviceData.measureVibration;
	deviceData.measureInterval = device?.measureInterval || deviceData.measureInterval;

	const min = 5;
	const max = 300;

	const setMeasureOnVibration = (measureVibration: boolean) => {
		deviceData.measureVibration = measureVibration;
		if ($controlBleDevice.connectionState === 2) {
			let command = measureVibration ? 'enableMeasureVibration' : 'disableMeasureVibration';
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, command)
				: control_bluetooth_device_android(device.id, command);
		} else {
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'connect')
				: control_bluetooth_device_android(device.id, 'connect');
		}
	};

	$: error = valueOutOfRange(deviceData.measureInterval, min, max)
		? $format('error_out_of_range', {
				default: `Value ${deviceData.measureInterval} is out of range. Please provide a value within the limits. (${min} — ${max})`,
				values: {
					value: deviceData.measureInterval,
					min,
					max,
					unit: $format('timer_text_sec', { default: 'sec' }),
				},
			})
		: '';
</script>

<OnboardingStepContainer>
	<svelte:fragment slot="text">
		{#if !$app}
			<Alert
				type="warning"
				isHeadingBold
				heading={$format('alert_heading_ble_devices_restricted', {
					default: 'Bluetooth Restricted',
				})}
				message={$format('alert_message_ble_devices_restricted', {
					default:
						'Bluetooth devices can be configured only via the Shelly Smart Control application.',
				})}
				class="mb-s"
			/>
		{:else}
			<DynamicAlert {showExpiredMsg} />
		{/if}
		<Text styleType="body-small" class="mb-m">
			{$format('text_set_time_interval_and_accelerometer', {
				default:
					'Set the time interval between valid measurements, and enable the accelerometer to automatically start measuring when movement or vibration is detected.',
			})}
		</Text>
	</svelte:fragment>

	<svelte:fragment slot="left">
		{#if deviceData.modeType === 'garage'}
			<Alert
				type="info"
				class="mb-m"
				heading={$format('alert_heading_garage_mode_text', {
					default:
						'This mode uses distance change and vibration to determine Opening and Closing. With vibration off, only the static Open and Closed states are shown.',
				})}
			/>
		{/if}
		<div class="mb-m d-flex align-items-center">
			<Toggle
				aria-label={$format('toggle_blu_setting', {
					default: 'Toggle setting',
				})}
				checked={deviceData.measureVibration}
				disabled={!initialized || !$app}
				on:click={(e) => setMeasureOnVibration(e.target.checked)}
				id="toggle-u48asf45s"
				size="m"
			/>
			<Text
				styleType="body-small"
				class={`ml-s ${classMap({ 'text-bold': deviceData.measureVibration })}`}
			>
				{$format('sub_settings_title_measurement_movement', {
					default: 'Measurements on movement/vibration',
				})}
			</Text>
		</div>

		<div class="mb-s d-flex align-items-center">
			<Text styleType="body-small" class="mr-s">
				{$format('text_measurement_interval', {
					default: 'Measurement interval (seconds)',
				})}
			</Text>
			<Popover type="hover" hideCloseBtn placement="top">
				<Icon class="text-primary" icon="fas fa-circle-info" size="s" />
				<Text slot="customContent" styleType="body-extra-small">
					{$format('popover_text_measurement_interval', {
						default:
							'The device can also take extra readings on button press or vibration. The interval resets from the last reading.',
					})}
				</Text>
			</Popover>
		</div>

		<Input
			aria-label={$format('input_measurement_interval', {
				default: 'Enter the measurement interval. Value must be between {min} — {max}!',
				values: { min, max },
			})}
			icon="fas fa-timer"
			label="30"
			bind:value={deviceData.measureInterval}
			type="number"
			step={1}
			{min}
			{max}
			info={$format('min_max_error_description', {
				default: 'Value must be between {min} and {max}.',
				values: { min, max },
			})}
			{error}
			disabled={!initialized || !$app}
			id="input-poj4f1j"
		/>
	</svelte:fragment>

	<svelte:fragment slot="right">
		<div class="vibration-setting-container">
			<img
				src="./images/onboarding/distance/vibration_setting.png"
				alt={$format('vibration_setting', { default: 'Vibration setting' })}
				width="200px"
				height="205px"
			/>
		</div>
	</svelte:fragment>
</OnboardingStepContainer>

<style>
	.vibration-setting-container {
		display: flex;
		justify-content: end;
	}

	@media (max-width: 639.98px) {
		.vibration-setting-container {
			margin-bottom: 24px;
			justify-content: center;
		}
	}
</style>
